<template>
  <div class="zipcode-page">
    <div class="content">
      <div class="container">
        <div class="page-header">
          <div class="row">
            <div class="col-lg-12">
              <h1 class="text-center">Neighborhoods in {{ zipcode }}</h1>
              <p class="text-center" v-if="!loading && neighborhoods.length > 0">
                Found {{ neighborhoods.length }} neighborhood{{ neighborhoods.length === 1 ? '' : 's' }} in zipcode {{ zipcode }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Loading State -->
        <div v-if="loading" class="loading-neighborhoods">
          <div class="text-center">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading neighborhoods...</span>
            </div>
            <p class="mt-2">Loading neighborhoods for {{ zipcode }}...</p>
          </div>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="alert alert-danger text-center">
            <h4>Unable to load neighborhoods</h4>
            <p>{{ error }}</p>
            <button @click="loadNeighborhoods" class="btn-base btn-primary">Try Again</button>
          </div>
        </div>
        
        <!-- Neighborhoods Grid -->
        <div v-else-if="neighborhoods.length > 0" class="neighborhoods-section">
          <div class="grid-base grid-responsive neighborhoods-grid">
            <div
              v-for="neighborhood in neighborhoods"
              :key="neighborhood.slug || neighborhood.name"
              class="card-base neighborhood-card"
              @click="selectNeighborhood(neighborhood)"
            >
              <div class="neighborhood-card-content">
                <h4 class="neighborhood-name">{{ neighborhood.name }}</h4>
                <p class="neighborhood-location">{{ neighborhood.city }}, {{ neighborhood.state }}</p>
                <p class="neighborhood-zipcode">{{ zipcode }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Inactive Zipcode -->
        <div v-else-if="zipcodeInactive" class="inactive-zipcode-message">
          <div class="inactive-zipcode-content">
            <h3>Zipcode Inactive</h3>
            <p>We have deactivated neighborhoods in {{ zipcode }} to focus on our core areas. Soon, there will be a way to pre-register for new locations.</p>
            <router-link to="/neighborhoods" class="btn-browse-neighborhoods">Browse Active Neighborhoods</router-link>
          </div>
        </div>

        <!-- Zipcode Not Found -->
        <div v-else-if="zipcodeNotFound" class="not-found-zipcode-message">
          <div class="not-found-zipcode-content">
            <h3>Zipcode Not Found</h3>
            <p>We couldn't find any neighborhoods for zipcode {{ zipcode }}. This zipcode may not be in our service area yet.</p>
            <router-link to="/neighborhoods" class="btn-browse-neighborhoods">Browse Available Neighborhoods</router-link>
          </div>
        </div>

        <!-- No Results State (fallback) -->
        <div v-else class="no-neighborhoods">
          <div class="text-center">
            <h3>No neighborhoods found</h3>
            <p>No neighborhoods were found for zipcode {{ zipcode }}.</p>
            <p>Try searching for a different zipcode or browse all <router-link to="/neighborhoods">neighborhoods</router-link>.</p>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import { getNeighborhoodsByZip, getZipCodeDetails } from '../utils/referenceDataUtils.js'
import { createErrorHandler } from '../utils/errorUtils.js'
import { capitalizeFirst, slugify } from '../utils/stringUtils.js'
import AppFooter from './shared/AppFooter.vue'

export default {
  name: 'ZipcodePage',
  components: {
    AppFooter
  },
  props: {
    zipcode: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const neighborhoods = ref([])
    const loading = ref(false)
    const error = ref(null)
    const zipcodeInactive = ref(false)
    const zipcodeNotFound = ref(false)
    const handleError = createErrorHandler('ZipcodePage')

    // Configure head/meta tags
    const pageTitle = computed(() => `Neighborhoods in ${props.zipcode} | SocialHood`)
    const pageDescription = computed(() =>
      `Discover neighborhoods in zipcode ${props.zipcode}. Connect with your local community on SocialHood.`
    )

    useHead({
      title: pageTitle,
      meta: [
        {
          name: 'description',
          content: pageDescription
        },
        {
          property: 'og:title',
          content: pageTitle
        },
        {
          property: 'og:description',
          content: pageDescription
        },
        {
          property: 'og:type',
          content: 'website'
        }
      ]
    })

    const loadNeighborhoods = async () => {
      loading.value = true
      error.value = null
      zipcodeInactive.value = false
      zipcodeNotFound.value = false

      try {
        // First, check if the zipcode itself exists and is active
        let zipcodeDetails = null
        try {
          zipcodeDetails = await getZipCodeDetails(props.zipcode)
        } catch (zipcodeErr) {
          // If zipcode doesn't exist in the database, mark as not found
          if (zipcodeErr.message?.includes('404') || zipcodeErr.message?.includes('not found')) {
            zipcodeNotFound.value = true
            neighborhoods.value = []
            loading.value = false
            return
          }
          // Otherwise continue to try fetching neighborhoods
        }

        // Check if zipcode itself is inactive
        if (zipcodeDetails && zipcodeDetails.active === false) {
          zipcodeInactive.value = true
          neighborhoods.value = []
          loading.value = false
          return
        }

        // Try to fetch neighborhoods in this zipcode
        const results = await getNeighborhoodsByZip(props.zipcode)

        if (results && results.length > 0) {
          // Check if any neighborhoods are active
          const hasActiveNeighborhoods = results.some(n => n.active !== false)

          if (hasActiveNeighborhoods) {
            // Filter to only show active neighborhoods
            neighborhoods.value = results.filter(n => n.active !== false)
          } else {
            // All neighborhoods are inactive
            zipcodeInactive.value = true
            neighborhoods.value = []
          }
        } else {
          // No neighborhoods found for this zipcode
          zipcodeNotFound.value = true
          neighborhoods.value = []
        }
      } catch (err) {
        const result = handleError(err, `Failed to load neighborhoods for ${props.zipcode}`)

        // Check if it's a 404 or similar
        if (err.message?.includes('404') || err.message?.includes('not found')) {
          zipcodeNotFound.value = true
        } else {
          error.value = result.error
        }
        neighborhoods.value = []
      } finally {
        loading.value = false
      }
    }

    const selectNeighborhood = (neighborhood) => {
      // Navigate to neighborhood page
      const state = capitalizeFirst(neighborhood.state) || 'Unknown'
      const city = slugify(neighborhood.city) || 'unknown'
      const slug = slugify(neighborhood.slug || neighborhood.name || '')
      
      router.push(`/neighborhood/${state}/${city}/${slug}`)
    }

    // Load neighborhoods when component mounts
    onMounted(() => {
      loadNeighborhoods()
    })

    // Reload neighborhoods when zipcode prop changes
    watch(() => props.zipcode, () => {
      if (props.zipcode) {
        loadNeighborhoods()
      }
    })

    return {
      neighborhoods,
      loading,
      error,
      zipcodeInactive,
      zipcodeNotFound,
      loadNeighborhoods,
      selectNeighborhood
    }
  }
}
</script>

<style scoped>
.zipcode-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 2rem 0;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.page-header p {
  color: #6c757d;
  font-size: 1.1rem;
}

.loading-neighborhoods {
  text-align: center;
  padding: 3rem 0;
}

.error-state {
  padding: 2rem 0;
}

.neighborhoods-section {
  margin-top: 2rem;
}

.neighborhoods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.neighborhood-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.neighborhood-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-color: #007bff;
}

.neighborhood-card-content {
  text-align: center;
}

.neighborhood-name {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.neighborhood-location {
  color: #6c757d;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.neighborhood-zipcode {
  color: #007bff;
  font-weight: 500;
  margin: 0;
  font-size: 0.9rem;
}

.no-neighborhoods {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

.no-neighborhoods h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.no-neighborhoods a {
  color: #007bff;
  text-decoration: none;
}

.no-neighborhoods a:hover {
  text-decoration: underline;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  color: #007bff;
}

/* Inactive Zipcode Message */
.inactive-zipcode-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.inactive-zipcode-content {
  text-align: center;
  padding: 50px 40px;
  background: linear-gradient(135deg, #fff3cd 0%, #ffc107 100%);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 2px solid #ffc107;
  max-width: 600px;
}

.inactive-zipcode-content h3 {
  font-size: 28px;
  color: #856404;
  margin-bottom: 20px;
  font-weight: 700;
}

.inactive-zipcode-content p {
  font-size: 18px;
  color: #856404;
  margin-bottom: 30px;
  line-height: 1.6;
}

.btn-browse-neighborhoods {
  display: inline-block;
  padding: 12px 30px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-browse-neighborhoods:hover {
  background-color: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

/* Not Found Zipcode Message */
.not-found-zipcode-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.not-found-zipcode-content {
  text-align: center;
  padding: 50px 40px;
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 2px solid #f5c6cb;
  max-width: 600px;
}

.not-found-zipcode-content h3 {
  font-size: 28px;
  color: #721c24;
  margin-bottom: 20px;
  font-weight: 700;
}

.not-found-zipcode-content p {
  font-size: 18px;
  color: #721c24;
  margin-bottom: 30px;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .neighborhoods-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .neighborhood-card {
    padding: 1rem;
  }

  .inactive-zipcode-content,
  .not-found-zipcode-content {
    padding: 30px 20px;
    margin: 0 10px;
  }

  .inactive-zipcode-content h3,
  .not-found-zipcode-content h3 {
    font-size: 24px;
  }

  .inactive-zipcode-content p,
  .not-found-zipcode-content p {
    font-size: 16px;
  }
}
</style>